import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { Box, Chip, Collapse, Typography } from "@mui/material";
import { type FC, type ReactNode, useCallback, useDeferredValue, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import type { TranslationKeys } from "../../../i18n/types";
import { DepartmentAlerts } from "../DepartmentAlerts/DepartmentAlerts";
import { useGetDepartmentAlerts } from "../DepartmentAlerts/hooks";
import { useManagementCenterFilters } from "../hooks";
import { ManagementCenterHeader } from "../ManagementCenterHeader";
import { MC_PAGE_BG } from "../utils";
import { HoursSummaryDetails } from "./HoursSummaryDetails";
import { HoursSummaryHeader } from "./HoursSummaryHeader";
import { HoursSummaryTable } from "./HoursSummaryTable";
import { useGetHoursSummary } from "./hooks";

type SectionKey = "hoursTable" | "alerts" | "details";

const SECTION_ITEMS = [
	{ key: "hoursTable", labelKey: "hours.summary.title" },
	{ key: "alerts", labelKey: "alerts.title" },
	{ key: "details", labelKey: "hours.summary.details" },
] as const satisfies readonly { key: SectionKey; labelKey: TranslationKeys }[];

export const HoursSummaryReport: FC = () => {
	const { t } = useTranslation();
	const { track, trackError } = useAnalytics();
	const {
		shiftHourOptions,
		shiftHoursUpdating,
		shiftHoursUnavailable,
		shiftHoursReady,
		sinceDate,
		endDate: rawEndDate,
		onStartDateChange,
		onEndDateChange,
		selectedRoles,
		onRolesChange,
		appliedRoleFilter,
		selectedJobs,
		onJobsChange,
		appliedJobFilter,
		selectedShiftHours,
		onShiftHoursChange,
		shiftHoursForApi,
		isActual,
		onActualToggleChange,
		laborCostEnabled,
		onLaborCostToggle,
		includeHolidays,
		onIncludeHolidaysToggle,
		excludeHolidaysForApi,
		includeLateScheduled,
		onIncludeLateScheduledToggle,
		includeLateScheduledForApi,
		estimateMissingWage,
		onEstimateMissingWageToggle,
		onEstimateMissingWageNoticeDismissed,
		otMode,
		onOtModeChange,
		onAlignToDayView,
		onSaveMcDefaults,
		onRestoreMcSettings,
	} = useManagementCenterFilters({ trackPrefix: "hours_summary", defaultDays: 30 });
	const endDate = rawEndDate?.minus({ day: 1 }); // So the summary won't include today (possibly no actual data yet)

	// Section collapse/expand state (independent from visibility)
	const [collapsedSections, setCollapsedSections] = useState<Set<SectionKey>>(new Set(["alerts", "details"]));
	const toggleCollapse = useCallback((key: SectionKey) => {
		setCollapsedSections((prev) => {
			const next = new Set(prev);
			if (next.has(key)) next.delete(key);
			else next.add(key);
			return next;
		});
	}, []);

	const detailsSectionOpen = !collapsedSections.has("details");

	// Table data query — skips employee detail DB queries for fast first load
	const {
		data,
		isLoading: hoursSummaryLoading,
		isError,
		error,
	} = useGetHoursSummary({
		sinceDate,
		endDate,
		isActual,
		roles: appliedRoleFilter,
		jobs: appliedJobFilter,
		shiftHours: shiftHoursForApi,
		excludeHolidays: excludeHolidaysForApi,
		includeLateScheduled: includeLateScheduledForApi,
		includeEmployeeDetails: false,
		otMode,
		enabled: shiftHoursReady,
	});
	const isLoading = hoursSummaryLoading || !shiftHoursReady;
	const deferredData = useDeferredValue(data);

	// Employee details query — only fires when details section is expanded
	const { data: detailsData, isLoading: detailsQueryLoading } = useGetHoursSummary({
		sinceDate,
		endDate,
		isActual,
		roles: appliedRoleFilter,
		jobs: appliedJobFilter,
		shiftHours: shiftHoursForApi,
		excludeHolidays: excludeHolidaysForApi,
		includeLateScheduled: includeLateScheduledForApi,
		includeEmployeeDetails: true,
		otMode,
		enabled: detailsSectionOpen && shiftHoursReady,
	});
	const detailsLoading = detailsQueryLoading || !shiftHoursReady;

	const alertsSectionOpen = !collapsedSections.has("alerts");
	const { data: alertsData, isLoading: alertsQueryLoading } = useGetDepartmentAlerts({
		sinceDate,
		endDate,
		isActual,
		roles: appliedRoleFilter,
		shiftHours: shiftHoursForApi,
		enabled: alertsSectionOpen && shiftHoursReady,
	});
	const alertsLoading = alertsQueryLoading || !shiftHoursReady;

	const hasTrackedViewed = useRef(false);
	useEffect(() => {
		if (!hasTrackedViewed.current) {
			track("hours_summary_viewed");
			hasTrackedViewed.current = true;
		}
	}, [track]);

	useEffect(() => {
		if (isError) trackError("fetch_hours_summary", { error_message: (error as Error)?.message });
	}, [isError, error, trackError]);

	return (
		<Box
			sx={{
				width: "100%",
				height: "100%",
				bgcolor: MC_PAGE_BG,
				overflow: "auto",
				padding: 2,
				overflowX: "hidden",
			}}
		>
			<HoursSummaryHeader />
			<ManagementCenterHeader
				defaultDays={30}
				onStartDateChange={onStartDateChange}
				onEndDateChange={onEndDateChange}
				isActual={isActual}
				onActualToggleChange={onActualToggleChange}
				onRolesChange={onRolesChange}
				selectedRoles={selectedRoles}
				selectedJobs={selectedJobs}
				onJobsChange={onJobsChange}
				shiftHourOptions={shiftHourOptions}
				shiftHoursUpdating={shiftHoursUpdating}
				shiftHoursUnavailable={shiftHoursUnavailable}
				selectedShiftHours={selectedShiftHours}
				onShiftHoursChange={onShiftHoursChange}
				laborCostEnabled={laborCostEnabled}
				onLaborCostToggle={onLaborCostToggle}
				includeHolidays={includeHolidays}
				onIncludeHolidaysToggle={onIncludeHolidaysToggle}
				includeLateScheduled={includeLateScheduled}
				onIncludeLateScheduledToggle={onIncludeLateScheduledToggle}
				estimateMissingWage={estimateMissingWage}
				onEstimateMissingWageToggle={onEstimateMissingWageToggle}
				onEstimateMissingWageNoticeDismissed={onEstimateMissingWageNoticeDismissed}
				otMode={otMode}
				onOtModeChange={onOtModeChange}
				onAlignToDayView={onAlignToDayView}
				onSaveMcDefaults={onSaveMcDefaults}
				onRestoreMcSettings={onRestoreMcSettings}
			/>
			{/* Section Selector */}
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					gap: 1,
					mt: 2,
				}}
			>
				<VisibilityOutlinedIcon sx={{ fontSize: 18, color: "text.secondary" }} />
				<Typography
					variant="caption"
					sx={{
						color: "text.secondary",
						fontWeight: 600,
					}}
				>
					{t("hours.summary.sections")}:
				</Typography>
				{SECTION_ITEMS.map(({ key, labelKey }) => {
					const active = collapsedSections.has(key);
					return (
						<Chip
							key={key}
							label={t(labelKey)}
							size="small"
							variant={active ? "filled" : "outlined"}
							onClick={() => toggleCollapse(key)}
							sx={{
								fontWeight: active ? 700 : 400,
								bgcolor: active ? "grey.800" : undefined,
								color: active ? "white" : undefined,
								borderColor: active ? "grey.800" : "grey.400",
								"&:hover": active ? { bgcolor: "grey.700" } : {},
							}}
						/>
					);
				})}
			</Box>
			<CollapsibleSection title={t("hours.summary.title")} isCollapsed={collapsedSections.has("hoursTable")} onToggle={() => toggleCollapse("hoursTable")}>
				<Box sx={{ opacity: deferredData !== data ? 0.6 : 1, transition: "opacity 0.15s" }}>
					<HoursSummaryTable data={deferredData} isLoading={isLoading} isActual={isActual} />
				</Box>
			</CollapsibleSection>
			<CollapsibleSection title={t("alerts.title")} isCollapsed={collapsedSections.has("alerts")} onToggle={() => toggleCollapse("alerts")}>
				<Box sx={{ opacity: deferredData !== data ? 0.6 : 1, transition: "opacity 0.15s" }}>
					<DepartmentAlerts data={alertsData} isLoading={alertsLoading} />
				</Box>
			</CollapsibleSection>
			<CollapsibleSection title={t("hours.summary.details")} isCollapsed={collapsedSections.has("details")} onToggle={() => toggleCollapse("details")}>
				<Box>
					<HoursSummaryDetails employeeDetails={detailsData?.employeeDetails} isLoading={detailsLoading} isActual={isActual} sinceDate={sinceDate} />
				</Box>
			</CollapsibleSection>
		</Box>
	);
};

// ── Collapsible Section Wrapper ─────────────────────────────────

const CollapsibleSection: FC<{ title: string; isCollapsed: boolean; onToggle: () => void; children: ReactNode }> = ({
	title,
	isCollapsed,
	onToggle,
	children,
}) => (
	<Box
		sx={{
			mt: 2,
		}}
	>
		<Box
			onClick={onToggle}
			sx={{
				display: "flex",
				alignItems: "center",
				gap: 1,
				cursor: "pointer",
				py: 0.5,
				"&:hover": { opacity: 0.8 },
				transition: "opacity 0.15s",
			}}
		>
			<KeyboardArrowDownIcon
				fontSize="small"
				sx={{
					transform: isCollapsed ? "rotate(-90deg)" : "rotate(0deg)",
					transition: "transform 0.2s",
					color: "text.secondary",
				}}
			/>
			<Typography
				variant="subtitle1"
				sx={{
					fontWeight: 600,
					color: "text.secondary",
				}}
			>
				{title}
			</Typography>
		</Box>
		<Collapse in={!isCollapsed} unmountOnExit>
			{children}
		</Collapse>
	</Box>
);
